import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import InputRadio from '@/components/ui/inputs/input-radio';
import { InputTextarea } from '@/components/ui/inputs/input-textarea';
import { useForm, Controller } from 'react-hook-form';
import { getSettingService, updateSettingService } from '@/services/SettingServices';
import { getCurrenciesService } from '@/services/CurrenciesServices';
import validationErrors from '@/lib/validationErrors';
import SidebarDrawer from '@/components/common/SidebarDrawer';
import { useSelector, useDispatch } from 'react-redux';
import PermissionCheck from '@/components/common/PermissionCheck';
import CurrencyCodeAdd from '@/components/common/CurrencyCodeAdd';
import Moment from 'moment';
import {
    DEFAULT_RECEIPT_PAPER_PRESET,
    DEFAULT_RECEIPT_PAPER_WIDTH_MM,
    getReceiptPaperPreset,
    getReceiptPreviewWidthPx,
    normalizeReceiptPaperWidthMm,
    RECEIPT_PAPER_PRESETS,
} from '@/lib/receiptPaper';
import {
    DEFAULT_RECEIPT_PRINT_METHOD,
    RECEIPT_PRINT_METHOD_OPTIONS,
} from '@/lib/thermalReceipt';

const SitePage = ({ pageTitle }) => {
    const selectRef = useRef(null);
    const {
        register,
        setValue,
        getValues,
        watch,
        formState: { errors },
        handleSubmit,
        control,
        reset,
    } = useForm();
    
    // Watch form values for receipt preview
    const watchedCompanyName = watch('company_name');
    const watchedCompanyAddress = watch('company_address');
    const watchedCompanyPhone = watch('company_phone');
    const watchedCompanyPhone2 = watch('company_phone_2');
    const watchedReceiptPaperPreset = watch('receipt_paper_preset');
    const watchedReceiptPaperWidthMm = watch('receipt_paper_width_mm');
    const watchedReceiptPrintMethod = watch('receipt_print_method');
    const dispatch = useDispatch();
    const isCustomReceiptPaperPreset = watchedReceiptPaperPreset === 'custom';
    const isDirectThermalPrintEnabled =
        (watchedReceiptPrintMethod || DEFAULT_RECEIPT_PRINT_METHOD) === 'qz';

    const [isLoading, setIsLoading] = useState(false);
    const [isFormSumbmit, setIsFormSumbmit] = useState(false);
    const [siteCurrencyPosition, setSiteCurrencyPosition] = useState(1);
    const [siteEmailVerification, setSiteEmailVerification] = useState(1);
    const [sitePhoneVerification, setSitePhoneVerification] = useState(1);
    const [siteOnlinePayment, setSiteOnlinePayment] = useState(1);
    const [currencyDataSource, setCurrencyDataSource] = useState([]);
    const [currencyOptionList, setCurrencyOptionList] = useState([]);
    const [currencyDataFilter, setCurrencyDataFilter] = useState({
        search: '',
        page: 1,
        limit: 100,
    });

    const filedList = [
        'company_name',
        'company_phone',
        'company_phone_2',
        'company_address',
        'company_website',
        'site_default_currency',
        'site_default_currency_symbol',
        'site_currency_position',
        'site_email_verification',
        'site_phone_verification',
        'site_digit_after_decimal_point',
        'site_google_map_key',
        'site_copyright',
        'site_online_payment_gateway',
        'site_default_sms_gateway',
        'site_food_preparation_time',
        'pos_invoice_print_count',
        'receipt_print_method',
        'thermal_printer_name',
        'receipt_paper_preset',
        'receipt_paper_width_mm',
    ];

    const smsGatewayOptionList = [
        { value: 'twilio', label: 'Twilio' },
        { value: 'clickatell', label: 'Clickatell' },
        { value: 'nexmo', label: 'Nexmo' },
    ];
    const receiptPaperOptionList = RECEIPT_PAPER_PRESETS.map((entry) => ({
        value: entry.value,
        label: entry.label,
    }));
    const receiptPrintMethodOptionList = RECEIPT_PRINT_METHOD_OPTIONS.map((entry) => ({
        value: entry.value,
        label: entry.label,
    }));

    const applyReceiptPaperPreset = (presetValue, fallbackWidthValue) => {
        const preset = getReceiptPaperPreset(presetValue);
        setValue('receipt_paper_preset', preset.value);
        if (preset.widthMm) {
            setValue('receipt_paper_width_mm', String(preset.widthMm));
            return;
        }
        const safeWidth = normalizeReceiptPaperWidthMm(fallbackWidthValue, preset.value);
        setValue('receipt_paper_width_mm', String(safeWidth));
    };

    useEffect(() => {
        getCurrencyData();
        setValue('site_currency_position', 1);
        setSiteCurrencyPosition(1);
        setValue('site_email_verification', 1);
        setSiteEmailVerification(1);
        setValue('site_phone_verification', 1);
        setSitePhoneVerification(1);
        setValue('site_online_payment_gateway', 1);
        setSiteOnlinePayment(1);
        setValue('receipt_print_method', DEFAULT_RECEIPT_PRINT_METHOD);
        setValue('thermal_printer_name', '');
        setValue('receipt_paper_preset', DEFAULT_RECEIPT_PAPER_PRESET);
        setValue('receipt_paper_width_mm', String(DEFAULT_RECEIPT_PAPER_WIDTH_MM));
        getDataByKey({
            key_list: filedList,
        });
    }, []);

    const formReset = async () => {
        try {
            var formData = getValues();
            for (const key in formData) {
                await setValue(key, '');
            }
            await reset();
            setValue('site_currency_position', 1);
            setSiteCurrencyPosition(1);
            setValue('site_email_verification', 1);
            setSiteEmailVerification(1);
            setValue('site_phone_verification', 1);
            setSitePhoneVerification(1);
            setValue('site_online_payment_gateway', 1);
            setSiteOnlinePayment(1);
            setValue('receipt_print_method', DEFAULT_RECEIPT_PRINT_METHOD);
            setValue('thermal_printer_name', '');
            setValue('receipt_paper_preset', DEFAULT_RECEIPT_PAPER_PRESET);
            setValue('receipt_paper_width_mm', String(DEFAULT_RECEIPT_PAPER_WIDTH_MM));
        } catch (error) {
            console.error('Error in onReset:', error);
        }
    };

    const handleChangeRadio = (event) => {
        if (event.target.name == 'site_currency_position') {
            setValue('site_currency_position', event.target.value);
            setSiteCurrencyPosition(event.target.value);
        } else if (event.target.name == 'site_email_verification') {
            setValue('site_email_verification', event.target.value);
            setSiteEmailVerification(event.target.value);
        } else if (event.target.name == 'site_phone_verification') {
            setValue('site_phone_verification', event.target.value);
            setSitePhoneVerification(event.target.value);
        } else if (event.target.name == 'site_online_payment_gateway') {
            setValue('site_online_payment_gateway', event.target.value);
            setSiteOnlinePayment(event.target.value);
        }
    };

    const getCurrencyData = () => {
        setIsLoading(true);
        dispatch(getCurrenciesService(currencyDataFilter))
            .then((res) => {
                var currencyDataArr = res?.data?.data?.data;
                var currencyOptionArray = [];
                currencyDataArr.forEach((element) => {
                    currencyOptionArray.push({
                        value: element?._id,
                        label: element?.name + ' (' + element.symbol + ')',
                    });
                });
                setCurrencyOptionList(currencyOptionArray);
                setCurrencyDataSource(currencyDataArr);
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    const getDataByKey = (keyList) => {
        setIsLoading(true);
        dispatch(getSettingService(keyList))
            .then((res) => {
                var formData = res?.data?.data;
                for (const key in formData) {
                    if (formData.hasOwnProperty(key) && formData[key] != '') {
                        setValue(key, formData[key]);
                    } else {
                        setValue(key, '');
                    }
                }
                if (formData) {
                    setValue('site_currency_position', formData.site_currency_position);
                    setSiteCurrencyPosition(formData.site_currency_position);
                    setValue('site_email_verification', formData.site_email_verification);
                    setSiteEmailVerification(formData.site_email_verification);
                    setValue('site_phone_verification', formData.site_phone_verification);
                    setSitePhoneVerification(formData.site_phone_verification);
                    setValue('site_online_payment_gateway', formData.site_online_payment_gateway);
                    setSiteOnlinePayment(formData.site_online_payment_gateway);
                    setValue(
                        'receipt_print_method',
                        formData.receipt_print_method || DEFAULT_RECEIPT_PRINT_METHOD
                    );
                    setValue('thermal_printer_name', formData.thermal_printer_name || '');
                    applyReceiptPaperPreset(
                        formData.receipt_paper_preset || DEFAULT_RECEIPT_PAPER_PRESET,
                        formData.receipt_paper_width_mm || DEFAULT_RECEIPT_PAPER_WIDTH_MM
                    );
                } else {
                    setValue('receipt_print_method', DEFAULT_RECEIPT_PRINT_METHOD);
                    setValue('thermal_printer_name', '');
                    applyReceiptPaperPreset(DEFAULT_RECEIPT_PAPER_PRESET, DEFAULT_RECEIPT_PAPER_WIDTH_MM);
                }
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    const onSelectCurrencyData = (currencyId) => {
        setValue('site_default_currency', currencyId);
        const selectCurrency = currencyDataSource.find((item) => item?._id === currencyId);
        setValue('site_default_currency_symbol', selectCurrency?.symbol);
    };

    const onSubmit = (data) => {
        setIsFormSumbmit(true);
        dispatch(updateSettingService(data))
            .then((res) => {
                getDataByKey({
                    key_list: filedList,
                });
                setIsFormSumbmit(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsFormSumbmit(false);
            });
    };

    const resolvedReceiptPaperPreset = watchedReceiptPaperPreset || DEFAULT_RECEIPT_PAPER_PRESET;
    const resolvedReceiptPaperWidthMm = normalizeReceiptPaperWidthMm(
        watchedReceiptPaperWidthMm,
        resolvedReceiptPaperPreset
    );
    const receiptPreviewWidthPx = getReceiptPreviewWidthPx(resolvedReceiptPaperWidthMm);

    return (
        <>
            <div id="company" className="db-card db-tab-div active">
                <div className="db-card-header">
                    <h3 className="db-card-title">{pageTitle}</h3>
                </div>
                <div className="db-card-body">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="mb-6 rounded-[28px] border border-[#156372]/15 bg-[linear-gradient(135deg,#fffdf6_0%,#fff7e6_58%,#fff1cc_100%)] p-5 shadow-[0_18px_50px_rgba(21,99,114,0.12)]">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                <div>
                                    <div className="inline-flex items-center gap-2 rounded-full border border-[#156372]/10 bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#156372]">
                                        <span className="h-2 w-2 rounded-full bg-[#156372]" />
                                        Third-Party Access
                                    </div>
                                    <h4 className="mt-2 text-lg font-semibold text-slate-900">
                                        Shimbirr connection, QR pairing, and permission sharing
                                    </h4>
                                    <p className="mt-2 text-sm text-slate-600">
                                        Open the dedicated Shimbirr settings page to enable secure profile-based sign-in, generate the pairing QR, and control what customer data your portal can request.
                                    </p>
                                </div>
                                <Link
                                    to="/admin/settings/shimbirr-auth"
                                    className="inline-flex items-center justify-center rounded-xl bg-[#156372] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#0f4d58]"
                                >
                                    Open Third-Party & Shimbirr
                                </Link>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Controller
                                control={control}
                                rules={{ required: true, minLength: 1 }}
                                name="company_name"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        size="sm"
                                        labelText={'Company Name (for receipts)'}
                                        type="text"
                                        id="company_name"
                                        placeholder="e.g., TabanFood"
                                        errorData={{ minLength: '1' }}
                                        errorType={errors?.company_name?.type}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                rules={{ required: true, minLength: 1 }}
                                name="company_phone"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        size="sm"
                                        labelText={'Company Phone (for receipts)'}
                                        type="text"
                                        id="company_phone"
                                        placeholder="e.g., +252 770655050"
                                        errorData={{ minLength: '1' }}
                                        errorType={errors?.company_phone?.type}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                rules={{ required: false }}
                                name="company_phone_2"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        labelStyleClassName="db-field-title"
                                        size="sm"
                                        labelText={'Company Phone 2 (for receipts)'}
                                        type="text"
                                        id="company_phone_2"
                                        placeholder="e.g., +252 123456789"
                                        errorType={errors?.company_phone_2?.type}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                rules={{ required: true, minLength: 1 }}
                                name="company_address"
                                render={({ field }) => (
                                    <InputTextarea
                                        {...field}
                                        rows={3}
                                        groupClassName="sm:col-span-2"
                                        className="h-auto"
                                        labelStyleClassName="db-field-title required"
                                        size="sm"
                                        labelText={'Company Address (for receipts)'}
                                        placeholder="e.g., maka ala mukarama taleh near premier bank"
                                        errorData={{ minLength: '1' }}
                                        errorType={errors?.company_address?.type}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                rules={{ required: false }}
                                name="company_website"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        labelStyleClassName="db-field-title"
                                        size="sm"
                                        labelText={'Company Website (for receipts)'}
                                        type="text"
                                        id="company_website"
                                        placeholder="e.g., https://taban.so"
                                        errorType={errors?.company_website?.type}
                                    />
                                )}
                            />

                            {/* <Controller
                                control={control}
                                rules={{ required: true }}
                                name="site_default_sms_gateway"
                                render={({ field }) => (
                                    <InputSelect
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        id={'site_default_sms_gateway'}
                                        labelText={'Default Sms Gateway'}
                                        value={smsGatewayOptionList.find((data) => data.value == getValues('site_default_sms_gateway')) || null}
                                        onChange={(e) => setValue('site_default_sms_gateway', e)}
                                        options={smsGatewayOptionList}
                                        errorType={errors?.site_default_sms_gateway?.type}
                                    />
                                )}
                            /> */}

                            <Controller
                                control={control}
                                rules={{ required: true }}
                                name="site_default_currency"
                                render={({ field }) => (
                                    <InputSelect
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        id={'site_default_currency'}
                                        labelText={'Default Currency'}
                                        value={currencyOptionList.find((data) => data.value == getValues('site_default_currency')) || null}
                                        onChange={(e) => onSelectCurrencyData(e)}
                                        options={currencyOptionList}
                                        errorType={errors?.site_default_currency?.type}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                rules={{ required: true, minLength: 1, maxLength: 50 }}
                                name="site_copyright"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        size="sm"
                                        labelText={'Copyright'}
                                        type="text"
                                        id="site_copyright"
                                        errorData={{ minLength: '1', maxLength: '50' }}
                                        errorType={errors?.site_copyright?.type}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                rules={{ required: true, minLength: 1, maxLength: 50 }}
                                name="site_google_map_key"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        size="sm"
                                        labelText={'Google Map Key'}
                                        type="text"
                                        id="site_google_map_key"
                                        errorData={{ minLength: '1', maxLength: '50' }}
                                        errorType={errors?.site_google_map_key?.type}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                rules={{ required: true, min: 1, max: 8 }}
                                name="site_digit_after_decimal_point"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        size="sm"
                                        labelText={'Digit After Decimal Point ( Ex: 0.00 )'}
                                        type="text"
                                        id="site_digit_after_decimal_point"
                                        errorData={{ min: '1', max: '8' }}
                                        errorType={errors?.site_digit_after_decimal_point?.type}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                rules={{ required: true, min: 1, max: 500 }}
                                name="site_food_preparation_time"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        size="sm"
                                        labelText={'Food Preparation Time ( In Minute )'}
                                        type="text"
                                        id="company_name"
                                        errorData={{ min: '1', max: '20' }}
                                        errorType={errors?.site_food_preparation_time?.type}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                rules={{ required: true, min: 1, max: 10 }}
                                name="pos_invoice_print_count"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        size="sm"
                                        labelText={'POS Invoice Print Count (for Kitchen & Customer)'}
                                        type="number"
                                        id="pos_invoice_print_count"
                                        placeholder="e.g., 2"
                                        errorData={{ min: '1', max: '10' }}
                                        errorType={errors?.pos_invoice_print_count?.type}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                rules={{ required: true }}
                                name="receipt_print_method"
                                render={({ field }) => (
                                    <InputSelect
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        id="receipt_print_method"
                                        labelText="Receipt Print Method"
                                        value={
                                            receiptPrintMethodOptionList.find(
                                                (entry) =>
                                                    entry.value ===
                                                    (getValues('receipt_print_method') ||
                                                        DEFAULT_RECEIPT_PRINT_METHOD)
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                            setValue(
                                                'receipt_print_method',
                                                selected?.value || DEFAULT_RECEIPT_PRINT_METHOD
                                            )
                                        }
                                        options={receiptPrintMethodOptionList}
                                        errorType={errors?.receipt_print_method?.type}
                                    />
                                )}
                            />
                            <div className="form-input-item">
                                <p className="text-xs text-slate-500">
                                    Browser print uses the normal print dialog. Direct thermal printer mode sends
                                    raw receipt commands through QZ Tray so the receipt length follows the content
                                    instead of a fixed browser page.
                                </p>
                            </div>

                            <Controller
                                control={control}
                                name="thermal_printer_name"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        labelStyleClassName="db-field-title"
                                        size="sm"
                                        labelText="Thermal Printer Name"
                                        type="text"
                                        id="thermal_printer_name"
                                        placeholder="Leave blank to use the computer's default printer"
                                        disabled={!isDirectThermalPrintEnabled}
                                        errorType={errors?.thermal_printer_name?.type}
                                    />
                                )}
                            />
                            <div className="form-input-item">
                                <p className="text-xs text-slate-500">
                                    {isDirectThermalPrintEnabled
                                        ? 'QZ Tray must be installed and running on this device. If printer name is blank, TabanFood will use the Windows default printer.'
                                        : 'Thermal printer name is only used when Direct thermal printer mode is enabled.'}
                                </p>
                            </div>

                            <Controller
                                control={control}
                                rules={{ required: true }}
                                name="receipt_paper_preset"
                                render={({ field }) => (
                                    <InputSelect
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        id="receipt_paper_preset"
                                        labelText="Receipt Paper Preset"
                                        value={
                                            receiptPaperOptionList.find(
                                                (entry) =>
                                                    entry.value ===
                                                    (getValues('receipt_paper_preset') || DEFAULT_RECEIPT_PAPER_PRESET)
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                            applyReceiptPaperPreset(
                                                selected?.value || DEFAULT_RECEIPT_PAPER_PRESET,
                                                getValues('receipt_paper_width_mm')
                                            )
                                        }
                                        options={receiptPaperOptionList}
                                        errorType={errors?.receipt_paper_preset?.type}
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                rules={{ required: true, min: 40, max: 120 }}
                                name="receipt_paper_width_mm"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        labelStyleClassName="db-field-title required"
                                        size="sm"
                                        labelText="Receipt Paper Width (mm)"
                                        type="number"
                                        id="receipt_paper_width_mm"
                                        placeholder="58 or 80"
                                        disabled={!isCustomReceiptPaperPreset}
                                        errorData={{ min: '40', max: '120' }}
                                        errorType={errors?.receipt_paper_width_mm?.type}
                                    />
                                )}
                            />
                            <div className="form-input-item">
                                <p className="text-xs text-slate-500">
                                    {isCustomReceiptPaperPreset
                                        ? 'Custom width is active. Enter the exact paper width in millimeters.'
                                        : 'Preset width is locked to the selected receipt paper size.'}
                                </p>
                            </div>

                            <div className="form-input-item">
                                <label className="db-field-title required" htmlFor="active">
                                    {'Currency Position'}
                                </label>
                                <div className="db-field-radio-group">
                                    <InputRadio
                                        id="($)-left"
                                        name="site_currency_position"
                                        labelText={'($) left'}
                                        value="1"
                                        onChange={() => handleChangeRadio({ target: { name: 'site_currency_position', value: 1 } })}
                                        checked={siteCurrencyPosition == '1' ? true : false}
                                    />
                                    <InputRadio
                                        id="right-($)"
                                        name="site_currency_position"
                                        labelText={'right ($)'}
                                        value="2"
                                        onChange={() => handleChangeRadio({ target: { name: 'site_currency_position', value: 2 } })}
                                        checked={siteCurrencyPosition == '2' ? true : false}
                                    />
                                </div>
                            </div>

                            <div className="form-input-item">
                                <label className="db-field-title required" htmlFor="active">
                                    {'Online Payment Gateway'}
                                </label>
                                <div className="db-field-radio-group">
                                    <InputRadio
                                        id="Enable"
                                        name="site_online_payment_gateway"
                                        labelText={'Enable'}
                                        value="1"
                                        onChange={() => handleChangeRadio({ target: { name: 'site_online_payment_gateway', value: 1 } })}
                                        checked={siteOnlinePayment == '1' ? true : false}
                                    />
                                    <InputRadio
                                        id="Disable"
                                        name="site_online_payment_gateway"
                                        labelText={'Disable'}
                                        value="2"
                                        onChange={() => handleChangeRadio({ target: { name: 'site_online_payment_gateway', value: 2 } })}
                                        checked={siteOnlinePayment == '2' ? true : false}
                                    />
                                </div>
                            </div>

                            <div className="form-input-item">
                                <label className="db-field-title required" htmlFor="active">
                                    {'Email Verification'}
                                </label>
                                <div className="db-field-radio-group">
                                    <InputRadio
                                        id="email-Enable"
                                        name="site_email_verification"
                                        labelText={'Enable'}
                                        value="1"
                                        onChange={() => handleChangeRadio({ target: { name: 'site_email_verification', value: 1 } })}
                                        checked={siteEmailVerification == '1' ? true : false}
                                    />
                                    <InputRadio
                                        id="email-Disable"
                                        name="site_email_verification"
                                        labelText={'Disable'}
                                        value="2"
                                        onChange={() => handleChangeRadio({ target: { name: 'site_email_verification', value: 2 } })}
                                        checked={siteEmailVerification == '2' ? true : false}
                                    />
                                </div>
                            </div>

                            <div className="form-input-item">
                                <label className="db-field-title required" htmlFor="active">
                                    {'Phone Verification'}
                                </label>
                                <div className="db-field-radio-group">
                                    <InputRadio
                                        id="phone-Enable"
                                        name="site_phone_verification"
                                        labelText={'Enable'}
                                        value="1"
                                        onChange={() => handleChangeRadio({ target: { name: 'site_phone_verification', value: 1 } })}
                                        checked={sitePhoneVerification == '1' ? true : false}
                                    />
                                    <InputRadio
                                        id="phone-Disable"
                                        name="site_phone_verification"
                                        labelText={'Disable'}
                                        value="2"
                                        onChange={() => handleChangeRadio({ target: { name: 'site_phone_verification', value: 2 } })}
                                        checked={sitePhoneVerification == '2' ? true : false}
                                    />
                                </div>
                            </div>
                            <PermissionCheck permsission={'update_settings'}>
                                <div className="sm:col-span-2">
                                    <button type="submit" className="db-btn text-white bg-primary">
                                        <i className="lab lab-save"></i>
                                        <span>Save</span>
                                        {isFormSumbmit ? (
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <></>
                                        )}
                                    </button>
                                </div>
                            </PermissionCheck>
                        </div>
                    </form>
                    
                    {/* Receipt Preview Section */}
                    <div className="mt-8 border-t pt-8">
                        <h3 className="db-card-title mb-4">Receipt Preview</h3>
                        <p className="mb-4 text-sm text-slate-600">
                            Current paper width: <strong>{resolvedReceiptPaperWidthMm}mm</strong>
                        </p>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Customer Receipt Preview */}
                            <div>
                                <h4 className="text-lg font-semibold mb-3 text-heading">Customer Receipt</h4>
                                <div
                                    className="border border-gray-300 rounded p-4 bg-white mx-auto"
                                    style={{ width: `${receiptPreviewWidthPx}px`, maxWidth: '100%' }}
                                >
                                    <ReceiptPreview 
                                        isKitchen={false}
                                        companyName={watchedCompanyName || 'Your Company Name'}
                                        companyAddress={watchedCompanyAddress || 'Your Company Address'}
                                        companyPhone={watchedCompanyPhone || '+1234567890'}
                                        companyPhone2={watchedCompanyPhone2 || ''}
                                        paperPreset={resolvedReceiptPaperPreset}
                                        paperWidthMm={resolvedReceiptPaperWidthMm}
                                    />
                                </div>
                            </div>
                            
                            {/* Kitchen Receipt Preview */}
                            <div>
                                <h4 className="text-lg font-semibold mb-3 text-heading">Kitchen Receipt</h4>
                                <div
                                    className="border border-gray-300 rounded p-4 bg-white mx-auto"
                                    style={{ width: `${receiptPreviewWidthPx}px`, maxWidth: '100%' }}
                                >
                                    <ReceiptPreview 
                                        isKitchen={true}
                                        companyName={watchedCompanyName || 'Your Company Name'}
                                        companyAddress={watchedCompanyAddress || 'Your Company Address'}
                                        companyPhone={watchedCompanyPhone || '+1234567890'}
                                        companyPhone2={watchedCompanyPhone2 || ''}
                                        paperPreset={resolvedReceiptPaperPreset}
                                        paperWidthMm={resolvedReceiptPaperWidthMm}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

// Receipt Preview Component
const ReceiptPreview = ({ isKitchen, companyName, companyAddress, companyPhone, companyPhone2, paperPreset, paperWidthMm }) => {
    // Sample order data for preview
    const sampleOrderData = {
        order_serial_no: 'ORD-001',
        order_datetime: new Date(),
        order_items: [
            {
                quantity: 2,
                item_id: '1',
                total_price: 25.00,
                tax_amount: 2.50,
                tax_rate: 10,
                item_variations: JSON.stringify({
                    _ids: ['var1'],
                    name: ['Large'],
                    attribute_name: ['Size']
                }),
                item_extras: JSON.stringify({
                    _ids: ['extra1'],
                    name: ['Extra Cheese']
                }),
                instruction: 'No onions please'
            },
            {
                quantity: 1,
                item_id: '2',
                total_price: 15.00,
                tax_amount: 0,
                tax_rate: 0,
                item_variations: '',
                item_extras: '',
                instruction: ''
            }
        ],
        items: [
            { _id: '1', name: 'Pizza Margherita' },
            { _id: '2', name: 'Caesar Salad' }
        ],
        withouttaxsubtotal: 40.00,
        total_tax: 2.50,
        subtotal: 40.00,
        discount: 5.00,
        total: 37.50,
        payment_method_id: '1',
        payment_gateway: { name: 'Cash' },
        payment_status: 2,
        waiter: { name: 'John Doe' },
        token: '123'
    };

    const paymentStatusOptionList = [
        { value: 1, label: 'Unpaid' },
        { value: 2, label: 'Paid' },
        { value: 3, label: 'Failed' },
        { value: 4, label: 'Waiting For Approval' },
    ];

    const getItemVariations = (itemVariations) => {
        try {
            const parsedItemVariations = typeof itemVariations === 'string' ? JSON.parse(itemVariations) : itemVariations;
            if (!parsedItemVariations || !parsedItemVariations._ids) {
                return null;
            }
            return parsedItemVariations._ids.map((element, index) => {
                const variationName = parsedItemVariations.name[index];
                const variationAttributeName = parsedItemVariations.attribute_name[index];
                return (
                    <span key={index}>
                        {variationAttributeName}: {variationName}
                        {index < parsedItemVariations._ids.length - 1 && ', '}
                    </span>
                );
            });
        } catch (error) {
            return null;
        }
    };

    const getItemExtra = (itemExtars) => {
        try {
            const parsedItemExtars = typeof itemExtars === 'string' ? JSON.parse(itemExtars) : itemExtars;
            if (!parsedItemExtars || !parsedItemExtars._ids) {
                return null;
            }
            return parsedItemExtars._ids.map((element, index) => {
                const extraName = parsedItemExtars.name[index];
                return (
                    <span key={index}>
                        {extraName}
                        {index < parsedItemExtars._ids.length - 1 && ', '}
                    </span>
                );
            });
        } catch (error) {
            return null;
        }
    };

    const resolvedPaperWidthMm = normalizeReceiptPaperWidthMm(paperWidthMm, paperPreset);
    const previewWidthPx = getReceiptPreviewWidthPx(resolvedPaperWidthMm);

    return (
        <div
            className={`receipt-preview ${isKitchen ? 'kitchen-receipt' : ''}`}
            style={{ width: `${previewWidthPx}px`, maxWidth: '100%', margin: '0 auto' }}
        >
            <style>{`
                .receipt-preview.kitchen-receipt .hide-on-kitchen,
                .receipt-preview.kitchen-receipt .hide-on-kitchen * {
                    display: none !important;
                }
                .receipt-preview.kitchen-receipt .price-header,
                .receipt-preview.kitchen-receipt .item-price {
                    display: none !important;
                }
                .receipt-preview:not(.kitchen-receipt) .show-on-kitchen-only {
                    display: none !important;
                }
                .receipt-preview.kitchen-receipt .show-on-kitchen-only {
                    display: block !important;
                }
            `}</style>
            
            {/* Kitchen Receipt Header */}
            <div className="text-center pb-3.5 border-b border-dashed border-gray-400 show-on-kitchen-only">
                <h3 className="text-2xl font-bold mb-1">Kitchen Receipt</h3>
            </div>
            
            {/* Company Info - Only on Customer Copy */}
            <div className="text-center pb-3.5 border-b border-dashed border-gray-400 hide-on-kitchen">
                {companyName && <h3 className="text-2xl font-bold mb-1">{companyName}</h3>}
                {companyAddress && companyAddress.trim() !== '' && (
                    <h4 className="text-sm font-normal mt-1">{companyAddress}</h4>
                )}
                {companyPhone && companyPhone.trim() !== '' && (
                    <h5 className="text-sm font-normal mt-1">Tel: {companyPhone}</h5>
                )}
                {companyPhone2 && companyPhone2.trim() !== '' && (
                    <h5 className="text-sm font-normal mt-1">Tel: {companyPhone2}</h5>
                )}
            </div>
            
            {/* Order Number - Only on Customer Copy */}
            {sampleOrderData?.order_serial_no && (
                <table className="w-full my-1.5 hide-on-kitchen">
                    <tbody>
                        <tr>
                            <td className="text-xs text-left py-0.5 text-heading">Order #{sampleOrderData.order_serial_no}</td>
                        </tr>
                    </tbody>
                </table>
            )}
            
            {/* Date - Visible on Both */}
            {sampleOrderData?.order_datetime && (
                <table className="w-full my-1.5">
                    <tbody>
                        <tr>
                            <td className="text-xs text-left py-0.5 text-heading">Date: {Moment(sampleOrderData.order_datetime).format('Y-M-D hh:mm:ss')}</td>
                        </tr>
                    </tbody>
                </table>
            )}
            
            <table className="w-full">
                <thead className="border-t border-b border-dashed border-gray-400">
                    <tr>
                        <th scope="col" className="py-1 font-normal text-xs capitalize text-left text-heading w-8">
                            qty
                        </th>
                        <th scope="col" className="py-1 font-normal text-xs capitalize flex items-center justify-between text-heading">
                            <span>Item Description</span>
                            <span className="price-header">Price</span>
                        </th>
                    </tr>
                </thead>
                <tbody className="border-b border-dashed border-gray-400">
                    {sampleOrderData?.order_items?.map((cart, index) => {
                        const matchedItem = sampleOrderData?.items?.find((item) => String(item._id) === String(cart.item_id));
                        const itemName = matchedItem?.name || 'Unknown Item';
                        
                        return (
                            <tr key={index}>
                                <td className="text-left font-normal align-top py-1">
                                    <p className="text-xs leading-5 text-heading">{cart?.quantity}</p>
                                </td>
                                <td className="text-left font-normal align-top py-1">
                                    <div className="flex items-center justify-between receipt-item-row">
                                        <h4 className="text-sm font-normal capitalize receipt-item-name">{itemName}</h4>
                                        <p className="text-xs leading-5 text-heading item-price">
                                            {cart.tax_rate && cart.tax_rate > 0 ? (
                                                <CurrencyCodeAdd price={cart.total_price - cart?.tax_amount} />
                                            ) : (
                                                <CurrencyCodeAdd price={cart.total_price} />
                                            )}
                                        </p>
                                    </div>
                                    {cart.item_variations && cart.item_variations != '' && (
                                        <p className="text-xs leading-5 font-normal text-heading max-w-[200px]">{getItemVariations(cart.item_variations)}</p>
                                    )}
                                    {cart.item_extras && cart.item_extras != '' && (
                                        <p className="text-xs leading-5 font-normal text-heading max-w-[200px]">{getItemExtra(cart.item_extras)}</p>
                                    )}
                                    {cart.instruction && <p className="text-xs leading-5 font-normal text-heading max-w-[200px]">Instruction: {cart.instruction}</p>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            
            {/* Totals - Only on Customer Copy */}
            <div className="py-2 pl-7 hide-on-kitchen">
                <table className="w-full">
                    <tbody>
                        {sampleOrderData?.total_tax && sampleOrderData.total_tax > 0 ? (
                            <>
                                <tr>
                                    <td className="text-xs text-left py-0.5 uppercase text-heading">Subtotal:</td>
                                    <td className="text-xs text-right py-0.5 text-heading">
                                        <CurrencyCodeAdd price={sampleOrderData?.withouttaxsubtotal} />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="text-xs text-left py-0.5 uppercase text-heading">Total VAT:</td>
                                    <td className="text-xs text-right py-0.5 text-heading">
                                        <CurrencyCodeAdd price={sampleOrderData?.total_tax} />
                                    </td>
                                </tr>
                            </>
                        ) : (
                            <tr>
                                <td className="text-xs text-left py-0.5 uppercase text-heading">Subtotal:</td>
                                <td className="text-xs text-right py-0.5 text-heading">
                                    <CurrencyCodeAdd price={sampleOrderData?.subtotal} />
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td className="text-xs text-left py-0.5 uppercase text-heading">Discount:</td>
                            <td className="text-xs text-right py-0.5 text-heading">
                                <CurrencyCodeAdd price={sampleOrderData?.discount || 0} />
                            </td>
                        </tr>
                        <tr>
                            <td className="text-xs text-left py-0.5 font-bold uppercase text-heading">Total:</td>
                            <td className="text-xs text-right py-0.5 font-bold text-heading">
                                <CurrencyCodeAdd price={sampleOrderData?.total} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            
            {/* Payment Info - Only on Customer Copy */}
            <div className="text-xs py-2 border-t border-b border-dashed border-gray-400 text-heading hide-on-kitchen">
                <p>Payment Type: {sampleOrderData?.payment_method_id && sampleOrderData?.payment_gateway ? sampleOrderData?.payment_gateway?.name : 'Cash'}</p>
                <p>Payment Status: {sampleOrderData?.payment_status && paymentStatusOptionList.find((da) => da.value === sampleOrderData?.payment_status)?.label}</p>
            </div>
            
            {/* Served by - visible on both */}
            <div className="text-center pt-2 pb-4">
                <p className="text-base font-bold leading-6 capitalize text-heading">
                    Served by: {sampleOrderData?.waiter?.name || 'John Doe'}
                </p>
            </div>
            
            {/* Token - Only on Customer Copy */}
            {sampleOrderData?.token && <h4 className="py-2 capitalize text-xl font-bold text-center border-b border-dashed border-gray-400 hide-on-kitchen">Token #{sampleOrderData?.token}</h4>}
            
            {/* Thank You - Only on Customer Copy */}
            <div className="text-center pt-2 pb-4 hide-on-kitchen">
                <p className="text-[11px] leading-[14px] capitalize text-heading">Thank You</p>
                <p className="text-[11px] leading-[14px] capitalize text-heading">please Come Again</p>
            </div>
            
            {/* Powered by - Only on Customer Copy */}
            <div className="flex flex-col items-end hide-on-kitchen">
                <h5 className="text-[8px] font-normal text-left w-[46px] leading-[10px]">Powered by</h5>
                <h6 className="text-xs font-normal leading-4">TabanFood</h6>
            </div>
        </div>
    );
};

export default SitePage;
